/**
 * Federation-Aware Workflow Hooks
 * 
 * Integrates Apollo Client federation with workflow state management
 * Preserves sophisticated caching and offline capabilities
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useApolloFederation, useWorkflowQuery, useWorkflowMutation, useWorkflowSubscription } from '../apollo/ApolloFederationClient';
import { gql, TypedDocumentNode } from '@apollo/client';

// GraphQL fragments for federated entities
const WORKFLOW_DEFINITION_FRAGMENT = gql`
  fragment WorkflowDefinitionFields on WorkflowDefinition {
    id
    name
    version
    description
    states {
      id
      name
      type
    }
    transitions {
      id
      from
      to
      condition
    }
    metadata
    createdAt
    updatedAt
  }
`;

const WORKFLOW_INSTANCE_FRAGMENT = gql`
  fragment WorkflowInstanceFields on WorkflowInstance {
    id
    definitionId
    status
    currentState
    input
    output
    startTime
    endTime
    tasks {
      id
      name
      status
      assignedTo
    }
    signals {
      id
      name
      payload
      timestamp
    }
    metadata
  }
`;

// Federated queries
const GET_WORKFLOW_DEFINITIONS = gql`
  ${WORKFLOW_DEFINITION_FRAGMENT}
  query GetWorkflowDefinitions($filter: WorkflowDefinitionFilter) {
    workflowDefinitions(filter: $filter) {
      ...WorkflowDefinitionFields
    }
  }
`;

const GET_WORKFLOW_INSTANCES = gql`
  ${WORKFLOW_INSTANCE_FRAGMENT}
  query GetWorkflowInstances($filter: WorkflowInstanceFilter) {
    workflowInstances(filter: $filter) {
      ...WorkflowInstanceFields
    }
  }
`;

const CREATE_WORKFLOW_DEFINITION = gql`
  ${WORKFLOW_DEFINITION_FRAGMENT}
  mutation CreateWorkflowDefinition($input: CreateWorkflowDefinitionInput!) {
    createWorkflowDefinition(input: $input) {
      ...WorkflowDefinitionFields
    }
  }
`;

const UPDATE_WORKFLOW_INSTANCE = gql`
  ${WORKFLOW_INSTANCE_FRAGMENT}
  mutation UpdateWorkflowInstance($id: ID!, $input: UpdateWorkflowInstanceInput!) {
    updateWorkflowInstance(id: $id, input: $input) {
      ...WorkflowInstanceFields
    }
  }
`;

// Real-time subscriptions
const WORKFLOW_INSTANCE_UPDATED = gql`
  ${WORKFLOW_INSTANCE_FRAGMENT}
  subscription WorkflowInstanceUpdated($instanceId: ID!) {
    workflowInstanceUpdated(instanceId: $instanceId) {
      ...WorkflowInstanceFields
    }
  }
`;

// Federation-aware workflow definitions hook
export const useWorkflowDefinitions = (filter?: any) => {
  const { data, loading, error, refetch } = useWorkflowQuery(GET_WORKFLOW_DEFINITIONS, { filter });
  const { invalidateCache } = useApolloFederation();
  const [createDefinition] = useWorkflowMutation(CREATE_WORKFLOW_DEFINITION);

  const create = useCallback(async (input: any) => {
    const result = await createDefinition(CREATE_WORKFLOW_DEFINITION, 
      { input },
      {
        optimisticResponse: {
          createWorkflowDefinition: {
            __typename: 'WorkflowDefinition',
            id: `temp-${Date.now()}`,
            ...input,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
        },
        entityType: 'WorkflowDefinition',
        entityId: input.id
      }
    );

    // Trigger sophisticated cache invalidation
    await invalidateCache('WorkflowDefinition', 'CREATE');
    
    return result;
  }, [createDefinition, invalidateCache]);

  return {
    definitions: data?.workflowDefinitions || [],
    loading,
    error,
    refetch,
    create
  };
};

// Federation-aware workflow instances hook
export const useWorkflowInstances = (filter?: any) => {
  const { data, loading, error, refetch } = useWorkflowQuery(GET_WORKFLOW_INSTANCES, { filter });
  const { invalidateCache, pendingOperations } = useApolloFederation();
  const [updateInstance] = useWorkflowMutation(UPDATE_WORKFLOW_INSTANCE);

  const update = useCallback(async (id: string, input: any) => {
    const result = await updateInstance(UPDATE_WORKFLOW_INSTANCE,
      { id, input },
      {
        optimisticResponse: {
          updateWorkflowInstance: {
            __typename: 'WorkflowInstance',
            id,
            ...input,
            updatedAt: new Date().toISOString()
          }
        },
        entityType: 'WorkflowInstance',
        entityId: id
      }
    );

    // Trigger cascade invalidation (will also invalidate related tasks)
    await invalidateCache('WorkflowInstance', 'UPDATE', id);
    
    return result;
  }, [updateInstance, invalidateCache]);

  // Get pending operations for these instances
  const pendingInstanceOperations = pendingOperations.filter(
    op => op.entityType === 'WorkflowInstance'
  );

  return {
    instances: data?.workflowInstances || [],
    loading,
    error,
    refetch,
    update,
    pendingOperations: pendingInstanceOperations
  };
};

// Real-time instance updates
export const useWorkflowInstanceSubscription = (instanceId: string) => {
  const { data, loading, error } = useWorkflowSubscription(
    WORKFLOW_INSTANCE_UPDATED,
    { instanceId }
  );

  return {
    instance: data?.workflowInstanceUpdated,
    loading,
    error
  };
};

// Federation-aware task management
export const useTaskManagement = () => {
  const { invalidateCache } = useApolloFederation();

  const assignTask = useCallback(async (taskId: string, userId: string) => {
    // This would use a federated mutation that might touch both Task and User services
    const ASSIGN_TASK = gql`
      mutation AssignTask($taskId: ID!, $userId: ID!) {
        assignTask(taskId: $taskId, userId: $userId) {
          id
          assignedTo
          status
          # Federation allows us to fetch user data across services
          assignee {
            id
            name
            email
          }
        }
      }
    `;

    const [assignMutation] = useWorkflowMutation(ASSIGN_TASK);
    
    const result = await assignMutation(ASSIGN_TASK,
      { taskId, userId },
      {
        optimisticResponse: {
          assignTask: {
            __typename: 'Task',
            id: taskId,
            assignedTo: userId,
            status: 'ASSIGNED',
            assignee: {
              __typename: 'User',
              id: userId,
              name: 'Loading...',
              email: 'loading@example.com'
            }
          }
        },
        entityType: 'Task',
        entityId: taskId
      }
    );

    // Cascade invalidation - updating a task should invalidate related workflow instance
    await invalidateCache('Task', 'UPDATE', taskId);
    
    return result;
  }, [invalidateCache]);

  return {
    assignTask
  };
};

// Sophisticated offline sync status
export const useOfflineSync = () => {
  const { isOnline, pendingOperations, processPendingOperations } = useApolloFederation();
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'error'>('idle');

  // Process operations when coming back online
  useEffect(() => {
    if (isOnline && pendingOperations.length > 0 && syncStatus === 'idle') {
      setSyncStatus('syncing');
      processPendingOperations()
        .then(() => setSyncStatus('idle'))
        .catch(() => setSyncStatus('error'));
    }
  }, [isOnline, pendingOperations, processPendingOperations, syncStatus]);

  return {
    isOnline,
    pendingOperations,
    syncStatus,
    pendingCount: pendingOperations.length,
    forcSync: processPendingOperations
  };
};

// Federation service health monitoring
export const useFederationHealth = () => {
  const { getServiceHealth } = useApolloFederation();
  const [serviceHealth, setServiceHealth] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  const checkHealth = useCallback(async () => {
    setLoading(true);
    try {
      const health = await getServiceHealth();
      setServiceHealth(health);
    } catch (error) {
      console.error('Service health check failed:', error);
    } finally {
      setLoading(false);
    }
  }, [getServiceHealth]);

  useEffect(() => {
    checkHealth();
    const interval = setInterval(checkHealth, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, [checkHealth]);

  return {
    serviceHealth,
    loading,
    checkHealth,
    allServicesHealthy: Object.values(serviceHealth).every(Boolean)
  };
};

// Regulatory Authority Federation Hooks
export const useAuthorities = (filter?: any) => {
  const GET_AUTHORITIES = gql`
    query GetAuthorities($filter: AuthorityFilter) {
      authorities(filter: $filter) {
        id
        name
        officialName
        authorityType
        jurisdiction {
          geographic {
            level
            territories
          }
          sectoral {
            industry
            subSectors
          }
        }
        status
        delegatedPowers {
          powerType
          description
          limitations
        }
        contactInfo {
          officialAddress {
            line1
            city
            state
            country
          }
          phone
          email
          website
        }
        parentAuthorityId
        childAuthorities {
          id
          name
          authorityType
        }
        metadata
        createdAt
        updatedAt
      }
    }
  `;

  const CREATE_AUTHORITY = gql`
    mutation CreateAuthority($input: CreateAuthorityInput!) {
      createAuthority(input: $input) {
        id
        name
        officialName
        authorityType
        status
        createdAt
        updatedAt
      }
    }
  `;

  const { data, loading, error, refetch } = useWorkflowQuery(GET_AUTHORITIES, { filter });
  const { invalidateCache } = useApolloFederation();
  const [createAuthority] = useWorkflowMutation(CREATE_AUTHORITY);

  const create = useCallback(async (input: any) => {
    const result = await createAuthority(CREATE_AUTHORITY, 
      { input },
      {
        optimisticResponse: {
          createAuthority: {
            __typename: 'Authority',
            id: `temp-${Date.now()}`,
            ...input,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }
        },
        entityType: 'Authority',
        entityId: input.id
      }
    );

    await invalidateCache('Authority', 'CREATE');
    return result;
  }, [createAuthority, invalidateCache]);

  return {
    authorities: data?.authorities || [],
    loading,
    error,
    refetch,
    create
  };
};

export const useRegulations = (filter?: any) => {
  const GET_REGULATIONS = gql`
    query GetRegulations($filter: RegulationFilter) {
      regulations(filter: $filter) {
        id
        code
        title
        shortName
        category
        jurisdiction {
          geographic {
            level
            territories
          }
        }
        issuingAuthority {
          id
          name
          officialName
          authorityType
        }
        effectiveDate
        status
        severity
        applicabilityScope {
          entityTypes
          businessActivities
          geographicScope {
            level
            territories
          }
        }
        requirements {
          id
          section
          requirement
          mandatory
          implementation {
            timeline
            resources {
              type
              description
            }
          }
        }
        penalties {
          violationType
          penaltyType
          minimumPenalty {
            amount
            currency
          }
          maximumPenalty {
            amount
            currency
          }
        }
        crossReferences {
          relatedRegulation
          relationshipType
          description
        }
        metadata
      }
    }
  `;

  const { data, loading, error, refetch } = useWorkflowQuery(GET_REGULATIONS, { filter });
  
  return {
    regulations: data?.regulations || [],
    loading,
    error,
    refetch
  };
};

// Comprehensive regulatory framework hook
export const useRegulatoryFramework = (workflowDefinitionId: string) => {
  const GET_REGULATORY_FRAMEWORK = gql`
    query GetRegulatoryFramework($workflowDefinitionId: ID!) {
      workflowDefinition(id: $workflowDefinitionId) {
        id
        name
        regulations {
          id
          code
          title
          category
          severity
          issuingAuthority {
            id
            name
            authorityType
          }
        }
        authorityId
        authority {
          id
          name
          officialName
          authorityType
          jurisdiction {
            geographic {
              level
              territories
            }
          }
          delegatedPowers {
            powerType
            description
          }
          parentAuthorityId
          parentAuthority {
            id
            name
            authorityType
          }
        }
        complianceLevel
        auditRequirements {
          id
          name
          frequency
          auditor {
            type
            qualifications {
              name
              issuingBody
            }
          }
        }
        jurisdictionalScope {
          authority
          jurisdiction {
            geographic {
              level
              territories
            }
          }
          effectiveDate
        }
        riskClassification
      }
    }
  `;

  const { data, loading, error, refetch } = useWorkflowQuery(GET_REGULATORY_FRAMEWORK, {
    workflowDefinitionId
  });

  const workflowDefinition = data?.workflowDefinition;

  // Authority hierarchy resolution
  const buildAuthorityHierarchy = useCallback((authority: any) => {
    const hierarchy = [authority];
    let current = authority;
    
    // Build upward hierarchy
    while (current?.parentAuthority) {
      hierarchy.unshift(current.parentAuthority);
      current = current.parentAuthority;
    }
    
    return hierarchy;
  }, []);

  // Regulation impact analysis
  const analyzeRegulationCompliance = useCallback(() => {
    if (!workflowDefinition?.regulations) return [];

    return workflowDefinition.regulations.map((regulation: any) => {
      const criticalRequirements = regulation.requirements?.filter(
        (req: any) => req.mandatory && regulation.severity === 'CRITICAL'
      );
      
      return {
        regulation,
        criticalRequirements: criticalRequirements || [],
        complianceGap: criticalRequirements?.length || 0,
        maxPenalty: regulation.penalties?.reduce(
          (max: number, penalty: any) => 
            Math.max(max, penalty.maximumPenalty?.amount || 0),
          0
        ) || 0
      };
    });
  }, [workflowDefinition]);

  return {
    workflowDefinition,
    authority: workflowDefinition?.authority,
    regulations: workflowDefinition?.regulations || [],
    authorityHierarchy: workflowDefinition?.authority 
      ? buildAuthorityHierarchy(workflowDefinition.authority)
      : [],
    complianceAnalysis: analyzeRegulationCompliance(),
    loading,
    error,
    refetch
  };
};

// Authority hierarchy management
export const useAuthorityHierarchy = (rootAuthorityId?: string) => {
  const GET_AUTHORITY_HIERARCHY = gql`
    query GetAuthorityHierarchy($rootAuthorityId: ID) {
      authorityHierarchy(rootAuthorityId: $rootAuthorityId) {
        id
        name
        authorityType
        level
        parentAuthorityId
        childAuthorities {
          id
          name
          authorityType
          childAuthorities {
            id
            name
            authorityType
          }
        }
        jurisdiction {
          geographic {
            level
            territories
          }
        }
        delegatedPowers {
          powerType
          description
          delegatedTo
        }
      }
    }
  `;

  const { data, loading, error } = useWorkflowQuery(GET_AUTHORITY_HIERARCHY, {
    rootAuthorityId
  });

  const buildFlatHierarchy = useCallback(() => {
    if (!data?.authorityHierarchy) return [];
    
    const flatten = (authorities: any[], level = 0): any[] => {
      return authorities.reduce((acc, authority) => {
        acc.push({ ...authority, hierarchyLevel: level });
        if (authority.childAuthorities?.length > 0) {
          acc.push(...flatten(authority.childAuthorities, level + 1));
        }
        return acc;
      }, []);
    };

    return flatten(Array.isArray(data.authorityHierarchy) ? data.authorityHierarchy : [data.authorityHierarchy]);
  }, [data]);

  return {
    hierarchy: data?.authorityHierarchy || [],
    flatHierarchy: buildFlatHierarchy(),
    loading,
    error
  };
};

// Comprehensive compliance dashboard hook
export const useComplianceDashboard = (tenantId: string) => {
  const GET_COMPLIANCE_DASHBOARD = gql`
    query GetComplianceDashboard($tenantId: String!) {
      complianceDashboard(tenantId: $tenantId) {
        overview {
          totalWorkflows
          compliantWorkflows
          nonCompliantWorkflows
          workflowsUnderReview
          overdueAudits
          upcomingRenewals
        }
        regulationSummary {
          totalRegulations
          criticalRegulations
          byCategory {
            category
            count
            criticalCount
          }
          bySeverity {
            severity
            count
          }
        }
        authoritySummary {
          totalAuthorities
          activeAuthorities
          byType {
            type
            count
          }
          byJurisdiction {
            level
            count
          }
        }
        riskAssessment {
          highRiskWorkflows {
            id
            name
            riskScore
            criticalRegulations
            overdueAudits
          }
          complianceGaps {
            workflowId
            workflowName
            missingRequirements
            estimatedCost
            timeline
          }
        }
        upcomingDeadlines {
          type
          description
          dueDate
          workflowId
          priority
        }
        auditStatus {
          completedThisYear
          scheduledThisYear
          overdue
          nextScheduled {
            workflowId
            workflowName
            scheduledDate
            auditor
          }
        }
      }
    }
  `;

  const { data, loading, error, refetch } = useWorkflowQuery(GET_COMPLIANCE_DASHBOARD, {
    tenantId
  });

  return {
    dashboard: data?.complianceDashboard,
    overview: data?.complianceDashboard?.overview,
    regulations: data?.complianceDashboard?.regulationSummary,
    authorities: data?.complianceDashboard?.authoritySummary,
    risks: data?.complianceDashboard?.riskAssessment,
    deadlines: data?.complianceDashboard?.upcomingDeadlines || [],
    audits: data?.complianceDashboard?.auditStatus,
    loading,
    error,
    refetch
  };
};

// Integration with existing enhanced components
export const useEnhancedWorkflowIntegration = () => {
  const definitions = useWorkflowDefinitions();
  const instances = useWorkflowInstances();
  const tasks = useTaskManagement();
  const offline = useOfflineSync();
  const health = useFederationHealth();

  // Bridge to existing enhanced component APIs
  return {
    // Workflow Definitions
    workflowDefinitions: definitions.definitions,
    loadingDefinitions: definitions.loading,
    createDefinition: definitions.create,
    
    // Workflow Instances  
    workflowInstances: instances.instances,
    loadingInstances: instances.loading,
    updateInstance: instances.update,
    
    // Tasks
    assignTask: tasks.assignTask,
    
    // Offline Status
    isOnline: offline.isOnline,
    pendingOperations: offline.pendingOperations,
    syncStatus: offline.syncStatus,
    
    // Federation Health
    serviceHealth: health.serviceHealth,
    allServicesHealthy: health.allServicesHealthy,
    
    // Combined loading state
    loading: definitions.loading || instances.loading || health.loading
  };
};